<script setup>
// FilterSelect.vue

import { computed, ref, nextTick, onUnmounted, watch, onMounted } from 'vue';
import { useElementBounding, useWindowSize } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import Button from 'next/button/Button.vue';
import { filterOperatorIcon } from '../operators.js';


const props = defineProps({
  options: {
    type: Array,
    required: true,
  },
  hideLabel: {
    type: Boolean,
    default: false,
  },
  hideIcon: {
    type: Boolean,
    default: false,
  },
  hideOptionIcon: {
    type: Boolean,
    default: false,
  },
  variant: {
    type: String,
    default: 'faded',
  },
  label: {
    type: String,
    default: null,
  },
  placeholder: {
    type: String,
    default: null,
  },
  showSearch: {
    type: Boolean,
    default: true,
  },
  showGroups: {
    type: Boolean,
    default: true,
  },
});

const { t } = useI18n();

const selected = defineModel({
  type: [String, Number],
  required: true,
});

const searchQuery = ref('');
const triggerRef = ref(null);
const dropdownRef = ref(null);
const isOpen = ref(false);

const { top, left, width, bottom } = useElementBounding(triggerRef);
const { height: windowHeight } = useWindowSize();

const selectedOption = computed(() => {
  return props.options.find(o => o.value === selected.value) || {};
});

const attributeIcons = {
  status: 'i-lucide-circle-dot',
  priority: 'i-lucide-flag',
  assignee_id: 'i-lucide-user',
  inbox_id: 'i-lucide-inbox',
  team_id: 'i-lucide-users',
  display_id: 'i-lucide-hash',
  campaign_id: 'i-lucide-megaphone',
  labels: 'i-lucide-tag',
  browser_language: 'i-lucide-globe',
  country_code: 'i-lucide-map-pin',
  referer: 'i-lucide-link',
  created_at: 'i-lucide-calendar-plus',
  last_activity_at: 'i-lucide-clock',
};

// Fonte única: operator novo em operators.js já chega aqui com ícone. Este
// componente mantinha uma cópia do mapa, e a cópia silenciosamente
// desatualizada mandava operador novo pro funil genérico.
const operatorIcons = filterOperatorIcon;

const getOptionIcon = option => {
  if (typeof option.icon === 'string') {
    return option.icon;
  }
  return operatorIcons[option.value] || attributeIcons[option.value] || 'i-lucide-filter';
};

const iconToRender = computed(() => {
  if (props.hideIcon) return null;
  if (!selectedOption.value || !selectedOption.value.value) {
    return 'i-lucide-filter';
  }
  return getOptionIcon(selectedOption.value);
});

const displayLabel = computed(() => {
  if (props.label) return props.label;
  if (props.hideLabel) return null;
  if (selectedOption.value && selectedOption.value.label) {
    return selectedOption.value.label;
  }
  return props.placeholder || null;
});

const filteredOptions = computed(() => {
  if (!searchQuery.value) return props.options;
  const query = searchQuery.value.toLowerCase();
  return props.options.filter(option =>
    option.label?.toLowerCase().includes(query)
  );
});

const groupedOptions = computed(() => {
  if (!props.showGroups) {
    return [{ name: null, options: filteredOptions.value }];
  }

  const groups = {
    standard: [],
    additional: [],
    customAttributes: [],
  };

  filteredOptions.value.forEach(option => {
    const model = option.attributeModel || 'standard';
    if (groups[model]) {
      groups[model].push(option);
    }
  });

  const result = [];
  if (groups.standard.length) {
    result.push({
      name: t('FILTER.GROUPS.STANDARD_FILTERS'),
      options: groups.standard,
    });
  }
  if (groups.additional.length) {
    result.push({
      name: t('FILTER.GROUPS.ADDITIONAL_FILTERS'),
      options: groups.additional,
    });
  }
  if (groups.customAttributes.length) {
    result.push({
      name: t('FILTER.GROUPS.CUSTOM_ATTRIBUTES'),
      options: groups.customAttributes,
    });
  }

  return result;
});

const dropdownStyle = computed(() => {
  const DROPDOWN_HEIGHT = 320;
  const spaceBelow = windowHeight.value - bottom.value;
  const openUp = spaceBelow < DROPDOWN_HEIGHT && top.value > DROPDOWN_HEIGHT;

  return {
    position: 'fixed',
    left: `${left.value}px`,
    minWidth: `${Math.max(width.value, 256)}px`,
    zIndex: 2147483647,
    ...(openUp
      ? { bottom: `${windowHeight.value - top.value}px` }
      : { top: `${bottom.value + 4}px` }
    ),
  };
});

const close = () => {
  isOpen.value = false;
};

const toggle = () => {
  isOpen.value = !isOpen.value;

  if (isOpen.value) {
    nextTick(() => {
      searchQuery.value = '';
    });
  }
};

const updateSelected = (newValue) => {
  selected.value = newValue;
  searchQuery.value = '';
  close(); // Fechar o dropdown após selecionar
};

// Fechar ao clicar fora
const handleClickOutside = (event) => {
  
  if (!isOpen.value) {
    return;
  }
  
  // Pega os elementos reais
  const triggerEl = triggerRef.value?.$el || triggerRef.value;
  const dropdownEl = dropdownRef.value;
  
  
  // Verifica se clicou no trigger
  if (triggerEl && triggerEl.contains(event.target)) {
    return;
  }
  
  // Verifica se clicou no dropdown
  if (dropdownEl && dropdownEl.contains(event.target)) {
    return;
  }
  
  close();
};

// Gerenciar listener
watch(isOpen, (newValue) => {
  
  if (newValue) {
    // Aguarda o próximo frame para evitar capturar o clique que abriu
    requestAnimationFrame(() => {
      document.addEventListener('click', handleClickOutside);
    });
  } else {
    document.removeEventListener('click', handleClickOutside);
  }
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
  <div class="relative inline-block">
    <Button
      ref="triggerRef"
      type="button"
      sm
      slate
      :variant="variant"
      :icon="iconToRender"
      :trailing-icon="true"
      :label="displayLabel"
      @click="toggle"
    />

    <Teleport to="body">
      <div
        v-if="isOpen"
        ref="dropdownRef"
        :style="dropdownStyle"
        class="bg-n-solid-2 border border-n-weak rounded-lg shadow-lg filter-select-dropdown"
        data-filter-dropdown
      >
        <div v-if="showSearch" class="p-2 border-b border-n-weak">
          <div class="relative">

            <input
              v-model="searchQuery"
              type="text"
              :placeholder="t('FILTER.SEARCH_PLACEHOLDER')"
              class="w-full pl-9 pr-3 py-2 text-sm bg-n-slate-2 border border-n-weak rounded-md text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-transparent"
              @click.stop
            />
          </div>
        </div>

        <div class="max-h-72 overflow-y-auto p-1">
          <template v-for="(group, groupIndex) in groupedOptions" :key="groupIndex">
            <div
              v-if="group.name"
              class="px-3 py-2 text-xs font-semibold text-n-slate-11 uppercase tracking-wide"
            >
              {{ group.name }}
            </div>

            <button
              v-for="option in group.options"
              :key="option.value"
              type="button"
              class="w-full flex items-center gap-2 px-3 py-2 text-sm text-n-slate-12 hover:bg-n-slate-3 rounded-md transition-colors cursor-pointer"
              :class="{
                'bg-n-blue-3 text-n-blue-11': option.value === selected,
              }"
              @click.stop="updateSelected(option.value)"
            >
              <span
                v-if="!hideOptionIcon"
                :class="getOptionIcon(option)"
                class="text-base flex-shrink-0"
                :style="{ color: option.value === selected ? 'var(--blue-9)' : 'var(--slate-11)' }"
              />
              <span class="flex-1 text-left truncate">{{ option.label }}</span>
              <span
                v-if="option.value === selected"
                class="i-lucide-check text-n-blue-9 flex-shrink-0"
              />
            </button>

            <div
              v-if="groupIndex < groupedOptions.length - 1 && group.options.length > 0"
              class="h-px bg-n-weak my-1"
            />
          </template>

          <div
            v-if="searchQuery && filteredOptions.length === 0"
            class="px-3 py-8 text-center text-sm text-n-slate-10"
          >
            {{ t('FILTER.NO_RESULTS') }}
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>